/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import {
    inject,
    observer
} from 'mobx-react';
import * as React from 'react';
import { RootStore } from '../../stores/root.store';
import { UserTeam } from '../../shared/domain';
import { ChatBox } from '../../components/chat-box';
import { AcknowledgementTypes, ChatMessage } from '../../models/chat';
import { Button, Typography } from '@mui/material';
import { CCSnackbar, CCSnackbarVariant } from '../../shared/components/cc-snackbar';

const ACKNOWLEDGE_MESSAGE = 'By using this service, you acknowledge and agree that your input and related data may be transmitted to OpenAI for processing in accordance with their applicable policies and terms of service.';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => createStyles({
    chatContainer: {
        paddingTop: '0.5em',
        height: 'calc(100vh - 100px)',
        width: '100%'
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        margin: '0 2em'
    },
    errorMessage: {
        width: '100%',
        color: 'red',
        fontWeight: 400,
        textAlign: 'center',
        margin: '1em'
    },
    sendButton: {
        marginTop: '1em'
    },
    aknowledgeContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        justifyContent: 'center',
        padding: '1em',
        height: '88vh',
        width: '100%'
    },
    aknowledgeMessage: {
        maxWidth: '35em'
    },
    snackbar: {
        top: '7em',
    },
});

interface Props extends WithStyles<typeof styles> {
    rootStore: RootStore;
    userTeam?: UserTeam;
}

interface States {
    snackbarVariant: CCSnackbarVariant;
    snackbarMessage: string;
    snackbarOpen: boolean;
    errorMessage: string;
    chatbotAccess: {
        url: string;
        token: string;
        userId: string;
    } | undefined;
}

@inject('rootStore')
@observer
class AiInsightsTab extends React.Component<Props, States> {
    public static defaultProps = {
    };
    
    state = {
        errorMessage: '',
        chatbotAccess: undefined as { url: string; token: string; userId: string } | undefined,
        snackbarVariant: 'info' as CCSnackbarVariant,
        snackbarMessage: '',
        snackbarOpen: false,
    };

    componentDidMount() {
        const {
            rootStore: {
                managerStore
            },
            userTeam
        } = this.props;

        managerStore.getChatbotAccess().then((accessData) => {
            this.setState({ chatbotAccess: accessData });
        });

        managerStore.getAcknowledgementStatus(AcknowledgementTypes.aiChat).catch((error) => {
            this.showError(error);
        });

        if (userTeam) {
            managerStore.getSuggestedPrompts(userTeam.teamId).catch((error) => {
                this.showError(error);
            });
        }
    }

    showSnackbar = (
        snackbarMessage: string,
        snackbarVariant: CCSnackbarVariant
    ) => {
        this.setState({
            snackbarVariant,
            snackbarMessage,
            snackbarOpen: true,
        });
    };

    showError = (errorMessage: string | undefined) => {
        let message;
        if (errorMessage) {
            message = errorMessage;
        } else {
            message = 'Unknown Error';
        }

        this.showSnackbar(message, 'error' as CCSnackbarVariant);
    };

    showWarning = (warningMessage: string | undefined) => {
        if (!warningMessage) {
            return;
        }

        this.showSnackbar(warningMessage, 'warning' as CCSnackbarVariant);
    };

    onSnackbarClosed = () => {
        this.setState({ snackbarOpen: false });
    };

    onAgreedToTerms = () => {
        const {
            rootStore: { 
                managerStore 
            }
        } = this.props;

        managerStore.sendAcknowledgeAccess(AcknowledgementTypes.aiChat).then((message) => {
            this.showSnackbar(message, 'success' as CCSnackbarVariant);
        }).catch((error) => {
            this.showError(error);
        });
    };

    onSendMessage = (message: ChatMessage) => {
        const {
            rootStore: { 
                managerStore 
            }
        } = this.props;

        managerStore.sendChatMessage(message);
    };

    public render() {
        const {
            classes,
            rootStore: { 
                managerStore: {
                    chatMessages,
                    isAgreedToAcknowledgeTerms,
                    suggestedPrompts
                }
            }
        } = this.props;

        const {
            chatbotAccess,
            snackbarVariant,
            snackbarMessage,
            snackbarOpen
        } = this.state;

        return (
            <>
                {
                    !isAgreedToAcknowledgeTerms ? (
                        <div className={classes.aknowledgeContainer}>
                            <Typography
                                className={classes.aknowledgeMessage}
                                variant="h6">
                                { ACKNOWLEDGE_MESSAGE }
                            </Typography>
                            <Button
                                className={classes.sendButton}
                                variant="contained"
                                color="primary"
                                onClick={this.onAgreedToTerms}
                            >
                                I Agree
                            </Button>
                        </div>
                    ) : (
                        <div className={classes.chatContainer}>
                            {
                                chatbotAccess && (
                                    <ChatBox
                                        chatbotAccess={chatbotAccess}
                                        onSendMessage={this.onSendMessage}
                                        chatbotMessages={chatMessages}
                                        suggestedPrompts={suggestedPrompts}
                                    />
                                )
                            }
                        </div>
                    )
                }
                <CCSnackbar
                    className={classes.snackbar}
                    variant={snackbarVariant}
                    message={snackbarMessage}
                    autoHideDuration={4000}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transition="right"
                    open={snackbarOpen}
                    onClose={this.onSnackbarClosed}
                />
            </>
        );
    }
}

const MUIComponent = withStyles(styles)(AiInsightsTab);
export { MUIComponent as AiInsightsTab };
